import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Plus, Minus, ArrowUpRight } from "lucide-react";
import { useLang } from "../lib/i18n";
import { PRODUCT_ORDER } from "../lib/images";
import { useAnnouncement } from "../lib/announcement";
import { Logo } from "./Logo";

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const location = useLocation();
  const { active: bannerActive } = useAnnouncement();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const [mobileProducts, setMobileProducts] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropdown(false);
    setMobileProducts(false);
  }, [location.pathname]);

  const productLinks = PRODUCT_ORDER.map(({ key, slug }) => ({
    label: t.products[key].name,
    to: `/produits/${slug}`,
  }));

  const links = [
    { label: t.nav.projects, to: "/realisations" },
    { label: t.nav.primes, to: "/primes" },
    { label: t.nav.about, to: "/a-propos" },
  ];

  const isActive = (to) => location.pathname === to;

  return (
    <>
      <motion.header
        className={`fixed left-0 right-0 z-[100] transition-colors duration-500 ${
          bannerActive ? "top-9" : "top-0"
        } ${
          scrolled || open ? "bg-ink/70 backdrop-blur-md border-b border-white/10" : "border-b border-transparent"
        }`}
        data-testid="header"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Logo className="h-[36px]" />

          <nav className="hidden lg:flex items-center gap-9">
            <Link
              to="/"
              data-testid="nav-home"
              onClick={(e) => {
                if (location.pathname === "/") {
                  e.preventDefault();
                  if (window.__lenis) window.__lenis.scrollTo(0);
                  else window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              className={`line-hover text-sm tracking-wide transition-colors duration-300 ${
                location.pathname === "/" ? "text-accent-yellow" : "text-white/75 hover:text-white"
              }`}
            >
              {t.nav.home}
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setDropdown(true)}
              onMouseLeave={() => setDropdown(false)}
            >
              <button
                type="button"
                data-testid="nav-products"
                className={`line-hover text-sm tracking-wide transition-colors duration-300 cursor-default ${
                  location.pathname.startsWith("/produits") ? "text-accent-yellow" : "text-white/75 hover:text-white"
                }`}
              >
                {t.nav.products}
              </button>
              <AnimatePresence>
                {dropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 top-full pt-3"
                    data-testid="products-dropdown"
                  >
                    <div className="flex flex-col min-w-[288px] bg-ink/70 backdrop-blur-xl border border-white/12 rounded-xl overflow-hidden shadow-2xl shadow-black/50">
                      {productLinks.map((l, i) => (
                        <Link
                          key={i}
                          to={l.to}
                          data-testid={`dropdown-link-${i}`}
                          className={`group flex items-center justify-between gap-8 px-5 py-3 text-sm tracking-wide text-white/75 hover:text-accent-yellow hover:bg-white/[0.04] transition-colors duration-200 ${
                            i < productLinks.length - 1 ? "border-b border-white/[0.07]" : ""
                          }`}
                        >
                          <span className="whitespace-nowrap">{l.label}</span>
                          <ArrowUpRight
                            size={14}
                            className="shrink-0 opacity-40 transition-all duration-200 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                          />
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {links.map((l, i) => (
              <Link
                key={i}
                to={l.to}
                data-testid={`nav-link-${i}`}
                className={`line-hover text-sm tracking-wide transition-colors duration-300 ${
                  isActive(l.to) ? "text-accent-yellow" : "text-white/75 hover:text-white"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <div className="hidden sm:flex items-center text-xs tracking-widest">
              {["fr", "nl"].map((code, i) => (
                <span key={code} className="flex items-center">
                  {i === 1 && <span className="text-white/25 mx-1">/</span>}
                  <button
                    onClick={() => setLang(code)}
                    data-testid={`lang-toggle-${code}`}
                    className={`uppercase transition-colors duration-300 ${
                      lang === code ? "text-accent-yellow" : "text-white/45 hover:text-white"
                    }`}
                  >
                    {code}
                  </button>
                </span>
              ))}
            </div>

            <Link
              to="/devis"
              data-testid="header-devis-btn"
              className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-xl border border-white/25 text-white text-sm font-medium hover:border-accent-yellow hover:text-accent-yellow transition-colors duration-300 whitespace-nowrap"
            >
              {t.hero.quoteCta}
            </Link>

            <Link
              to="/rendez-vous"
              data-testid="header-quote-btn"
              className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-xl bg-accent-yellow text-ink text-sm font-semibold hover:bg-white transition-colors duration-300 whitespace-nowrap"
            >
              {t.nav.quote}
            </Link>

            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="menu-open-btn"
              className="lg:hidden text-white p-1"
              aria-label="Menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <>
            <div
              className="lg:hidden fixed inset-0 z-[105]"
              onClick={() => setOpen(false)}
              data-testid="mobile-menu-overlay"
            />
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className={`lg:hidden fixed ${
                bannerActive ? "top-[112px]" : "top-[76px]"
              } left-4 right-4 z-[110] rounded-2xl border border-white/12 bg-ink/70 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden`}
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col p-2">
                <Link
                  to="/"
                  data-testid="mobile-nav-home"
                  className={`px-4 py-3 text-sm tracking-wide rounded-lg transition-colors duration-200 ${
                    location.pathname === "/" ? "text-accent-yellow" : "text-white/80 hover:text-white hover:bg-white/[0.04]"
                  }`}
                >
                  {t.nav.home}
                </Link>

                <div>
                  <button
                    onClick={() => setMobileProducts((v) => !v)}
                    data-testid="mobile-products-toggle"
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm tracking-wide rounded-lg transition-colors duration-200 ${
                      location.pathname.startsWith("/produits") ? "text-accent-yellow" : "text-white/80 hover:text-white hover:bg-white/[0.04]"
                    }`}
                  >
                    {t.nav.products}
                    {mobileProducts ? <Minus size={18} /> : <Plus size={18} />}
                  </button>
                  <AnimatePresence>
                    {mobileProducts && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="ml-4 my-1 pl-3 border-l border-white/10 flex flex-col">
                          {productLinks.map((l, i) => (
                            <Link
                              key={i}
                              to={l.to}
                              data-testid={`mobile-product-link-${i}`}
                              className="px-3 py-2.5 text-sm text-white/55 hover:text-accent-yellow rounded-lg hover:bg-white/[0.04] transition-colors duration-200"
                            >
                              {l.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {links.map((l, i) => (
                  <Link
                    key={i}
                    to={l.to}
                    data-testid={`mobile-nav-link-${i}`}
                    className={`px-4 py-3 text-sm tracking-wide rounded-lg transition-colors duration-200 ${
                      isActive(l.to) ? "text-accent-yellow" : "text-white/80 hover:text-white hover:bg-white/[0.04]"
                    }`}
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>

              <div className="border-t border-white/10 p-3 flex flex-col gap-3">
                <div className="flex items-center text-xs tracking-widest px-1">
                  {["fr", "nl"].map((code, i) => (
                    <span key={code} className="flex items-center">
                      {i === 1 && <span className="text-white/25 mx-2">/</span>}
                      <button
                        onClick={() => setLang(code)}
                        data-testid={`mobile-lang-${code}`}
                        className={`uppercase transition-colors duration-200 ${lang === code ? "text-accent-yellow" : "text-white/45 hover:text-white"}`}
                      >
                        {code}
                      </button>
                    </span>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/devis"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-devis-btn"
                    className="text-center px-4 py-2.5 rounded-xl border border-white/25 text-white text-sm font-medium hover:border-accent-yellow hover:text-accent-yellow transition-colors duration-200"
                  >
                    {t.hero.quoteCta}
                  </Link>
                  <Link
                    to="/rendez-vous"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-rdv-btn"
                    className="text-center px-4 py-2.5 rounded-xl bg-accent-yellow text-ink text-sm font-semibold hover:bg-white transition-colors duration-200"
                  >
                    {t.nav.quote}
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
